import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Settings as SettingsIcon,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  Save,
  TestTube2,
  Globe,
  Key,
  RefreshCw,
  Database,
  Activity,
  Users,
  TrendingUp,
  Package
} from 'lucide-react';

const Badge = ({ children, variant = 'default' }) => {
  const variants = {
    default: 'bg-gray-700 text-gray-200',
    success: 'bg-green-900/50 text-green-400 border border-green-700',
    warning: 'bg-amber-900/50 text-amber-400 border border-amber-700',
    error: 'bg-red-900/50 text-red-400 border border-red-800',
    info: 'bg-blue-900/50 text-blue-400 border border-blue-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${variants[variant]}`}>
      {children}
    </span>
  );
};

const StatusBadge = ({ mode, status }) => {
  if (mode === 'mock' || status === 'mock_mode') {
    return <Badge variant="warning"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" /> Mock Mode</Badge>;
  }
  if (status === 'connected') {
    return <Badge variant="success"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> Connected</Badge>;
  }
  if (status === 'error') {
    return <Badge variant="error"><span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" /> Error</Badge>;
  }
  return <Badge variant="info"><span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" /> {status || 'Unknown'}</Badge>;
};

const SampleDataPanel = ({ data }) => {
  const m = data.metrics || {};
  const accounts = data.sample_accounts || [];
  const plans = data.plans || [];

  return (
    <div className="space-y-3 mt-3">
      {/* Metrics strip */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: 'Total Accounts', value: data.total_accounts, icon: Users },
          { label: 'Active Subs', value: m.active_subscriptions, icon: Activity },
          { label: 'MRR', value: m.mrr != null ? `A$${m.mrr.toLocaleString()}` : '—', icon: TrendingUp },
          { label: 'Churn Rate', value: m.churn_rate != null ? `${m.churn_rate.toFixed(1)}%` : '—', icon: TrendingUp },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="bg-black/40 rounded-lg p-2.5 flex items-center gap-2">
            <Icon className="w-3.5 h-3.5 text-primary/70 flex-shrink-0" />
            <div>
              <div className="text-xs text-muted-foreground">{label}</div>
              <div className="text-sm font-semibold">{value ?? '—'}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Sample accounts */}
      {accounts.length > 0 && (
        <div>
          <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
            <Users className="w-3 h-3" /> Sample Accounts
          </div>
          <div className="space-y-1">
            {accounts.map((a) => (
              <div key={a.account_id} className="flex items-center justify-between bg-black/30 rounded px-3 py-1.5 text-xs">
                <div>
                  <span className="font-medium text-foreground/90">{a.business_name}</span>
                  <span className="text-muted-foreground ml-2">{a.trade_type} · {a.location}</span>
                </div>
                <Badge variant={
                  a.subscription_status === 'active' ? 'success' :
                  a.subscription_status === 'trialing' ? 'info' :
                  a.subscription_status === 'canceled' ? 'error' : 'warning'
                }>
                  {a.subscription_status}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plans */}
      {plans.length > 0 && (
        <div>
          <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
            <Package className="w-3 h-3" /> Available Plans
          </div>
          <div className="flex flex-wrap gap-1.5">
            {plans.map((p) => (
              <span key={p.plan_id} className="bg-black/30 border border-border rounded px-2 py-1 text-xs">
                {p.name} — <span className="text-primary">A${p.price}/{p.interval}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] text-muted-foreground/60">
        Pulled at {new Date(data.pulled_at).toLocaleTimeString()} · mode: {data.connector?.mode}
      </div>
    </div>
  );
};

const ConnectorCard = ({ platform, displayName, region, icon, initialData, onSaved }) => {
  const [apiUrl, setApiUrl] = useState(initialData?.api_url || '');
  const [apiKey, setApiKey] = useState('');
  const [mode, setMode] = useState(initialData?.mode || 'mock');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sampling, setSampling] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [sampleData, setSampleData] = useState(null);
  const [sampleError, setSampleError] = useState(null);
  const [status, setStatus] = useState(initialData?.status || {});

  useEffect(() => {
    if (initialData) {
      setApiUrl(initialData.api_url || '');
      setMode(initialData.mode || 'mock');
      setStatus(initialData.status || {});
    }
  }, [initialData]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const payload = { api_url: apiUrl, mode };
      if (apiKey) payload.api_key = apiKey;
      const res = await axios.put(`/api/settings/connectors/${platform}`, payload);
      setSaveMsg({ ok: true, text: res.data.message || 'Saved successfully' });
      setStatus(res.data.status || status);
      if (onSaved) onSaved(platform, res.data.status);
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (err) {
      setSaveMsg({ ok: false, text: err.response?.data?.detail || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`/api/settings/connectors/${platform}/test`);
      setTestResult({ ok: res.data.status !== 'error', data: res.data });
    } catch (err) {
      setTestResult({ ok: false, data: { message: err.response?.data?.detail || 'Connection failed' } });
    } finally {
      setTesting(false);
    }
  };

  const handleSampleData = async () => {
    setSampling(true);
    setSampleData(null);
    setSampleError(null);
    try {
      const res = await axios.get(`/api/platforms/${platform}/sample`);
      setSampleData(res.data);
    } catch (err) {
      setSampleError(err.response?.data?.detail || 'Failed to pull sample data');
    } finally {
      setSampling(false);
    }
  };

  const modeOptions = [
    { value: 'mock', label: 'Mock Mode', desc: 'Simulated data (safe for dev)' },
    { value: 'test', label: 'Sandbox', desc: 'Test environment' },
    { value: 'live', label: 'Live', desc: 'Production API' },
  ];

  return (
    <Card className="bg-card border-border card-glow">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-2xl">{icon}</div>
            <div>
              <CardTitle className="text-base">{displayName}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">{region}</p>
            </div>
          </div>
          <StatusBadge mode={status?.mode || mode} status={status?.status} />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Mode Selector */}
        <div>
          <label className="text-sm font-medium mb-2 block">Connector Mode</label>
          <div className="grid grid-cols-3 gap-2">
            {modeOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setMode(opt.value)}
                className={`p-2 rounded-lg border text-left transition-all text-xs ${
                  mode === opt.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-black/20 text-muted-foreground hover:border-primary/50'
                }`}
              >
                <div className="font-medium">{opt.label}</div>
                <div className="text-[10px] mt-0.5 opacity-70">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* API URL */}
        <div>
          <label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5" /> API Base URL
          </label>
          <input
            type="url"
            value={apiUrl}
            onChange={e => setApiUrl(e.target.value)}
            placeholder={`https://api.${platform}.com/v1`}
            className="w-full bg-black/30 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary/50 placeholder:text-muted-foreground/50"
            disabled={mode === 'mock'}
          />
        </div>

        {/* API Key */}
        <div>
          <label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
            <Key className="w-3.5 h-3.5" /> API Key
            {initialData?.api_key_set && (
              <span className="text-xs text-green-400 font-normal">(set: {initialData.api_key_masked})</span>
            )}
          </label>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder={initialData?.api_key_set ? 'Enter new key to replace...' : 'Enter API key...'}
              className="w-full bg-black/30 border border-border rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:border-primary/50 placeholder:text-muted-foreground/50"
              disabled={mode === 'mock'}
            />
            <button
              onClick={() => setShowKey(!showKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              type="button"
            >
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {mode === 'mock' && (
            <p className="text-xs text-muted-foreground mt-1">Credentials not required in mock mode.</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 bg-primary/20 hover:bg-primary/30 border border-primary/50 text-primary rounded-lg px-4 py-2 text-sm font-medium transition-all disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save Config'}
          </button>
          <button
            onClick={handleTest}
            disabled={testing}
            className="flex items-center gap-2 bg-black/30 hover:bg-black/50 border border-border rounded-lg px-4 py-2 text-sm text-muted-foreground transition-all disabled:opacity-50"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />}
            Test
          </button>
        </div>

        {/* Save message */}
        {saveMsg && (
          <div className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${
            saveMsg.ok ? 'bg-green-900/30 text-green-400 border border-green-800' : 'bg-red-900/30 text-red-400 border border-red-800'
          }`}>
            {saveMsg.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {saveMsg.text}
          </div>
        )}

        {/* Test result */}
        {testResult && (
          <div className={`rounded-lg px-3 py-2 text-sm border ${
            testResult.ok ? 'bg-green-900/30 text-green-400 border-green-800' : 'bg-red-900/30 text-red-400 border-red-800'
          }`}>
            <div className="flex items-center gap-2 font-medium">
              {testResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {testResult.ok ? 'Connection OK' : 'Connection Failed'}
            </div>
            {testResult.data?.message && (
              <p className="text-xs mt-1 opacity-80">{testResult.data.message}</p>
            )}
          </div>
        )}

        {/* Sample Data Pull — SiteDesk only */}
        {platform === 'sitedesk' && (
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium flex items-center gap-1.5">
                <Database className="w-3.5 h-3.5 text-primary" />
                Sample Data
              </span>
              <button
                onClick={handleSampleData}
                disabled={sampling}
                className="flex items-center gap-1.5 text-xs bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary rounded-lg px-3 py-1.5 transition-all disabled:opacity-50"
              >
                {sampling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {sampling ? 'Pulling...' : 'Pull Sample Data'}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              Verify the Control Tower can connect and retrieve data from SiteDesk.
            </p>

            {sampleError && (
              <div className="flex items-center gap-2 text-xs text-red-400 bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">
                <XCircle className="w-3.5 h-3.5" /> {sampleError}
              </div>
            )}

            {sampleData && <SampleDataPanel data={sampleData} />}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Settings = () => {
  const { user, isAdmin } = useAuth();
  const [connectors, setConnectors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);

  const fetchConnectors = async () => {
    setLoading(true);
    setError(null);
    try {
      const [connRes, healthRes] = await Promise.all([
        axios.get('/api/settings/connectors'),
        axios.get('/api/health'),
      ]);
      setConnectors(connRes.data);
      setHealth(healthRes.data);
    } catch (err) {
      setError('Failed to load connector settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchConnectors();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div data-testid="settings-page">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Admin Access Required</h2>
          <p className="text-muted-foreground">You need admin privileges to access settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">System configuration and platform connector management</p>
      </div>

      <div className="grid gap-6">

        {/* Health overview */}
        {health && (
          <Card className="bg-card border-border card-glow">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" /> System Health
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(health.components || {}).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-2 bg-black/30 rounded-lg px-3 py-2">
                    <span className={`w-2 h-2 rounded-full ${val === 'ok' ? 'bg-green-400' : val === 'mock' ? 'bg-amber-400' : 'bg-blue-400'}`} />
                    <div>
                      <div className="text-xs text-muted-foreground capitalize">{key}</div>
                      <div className="text-xs font-medium">{val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Platform Connectors */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Platform Connectors</h2>
            <button
              onClick={fetchConnectors}
              disabled={loading}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {loading && !connectors ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 text-red-400 bg-red-900/20 border border-red-800 rounded-lg px-4 py-3">
              <XCircle className="w-4 h-4" />
              {error}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ConnectorCard
                platform="kazilink"
                displayName="KaziLink"
                region="Kenya · East Africa"
                icon="🇰🇪"
                initialData={connectors?.kazilink}
                onSaved={fetchConnectors}
              />
              <ConnectorCard
                platform="sitedesk"
                displayName="SiteDesk"
                region="Perth, Western Australia"
                icon="🇦🇺"
                initialData={connectors?.sitedesk}
                onSaved={fetchConnectors}
              />
            </div>
          )}
        </div>

        {/* System Information */}
        <Card className="bg-card border-border card-glow">
          <CardHeader>
            <CardTitle className="text-base">System Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-black/30 rounded-lg">
              <div className="text-sm text-muted-foreground">AI Model</div>
              <div className="font-medium">OpenAI GPT-4o-mini</div>
            </div>
            <div className="p-4 bg-black/30 rounded-lg">
              <div className="text-sm text-muted-foreground">Image Generation</div>
              <div className="font-medium">DALL-E 3</div>
            </div>
            <div className="p-4 bg-black/30 rounded-lg">
              <div className="text-sm text-muted-foreground">Website Scanner</div>
              <div className="font-medium">HTTP + Rule-based + AI Analysis</div>
            </div>
            <div className="p-4 bg-black/30 rounded-lg">
              <div className="text-sm text-muted-foreground">Database</div>
              <div className="font-medium">PostgreSQL (asyncpg)</div>
            </div>
          </CardContent>
        </Card>

        {/* Daily Limits */}
        <Card className="bg-card border-border card-glow">
          <CardHeader>
            <CardTitle className="text-base">Daily Limits</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-black/30 rounded-lg text-center">
              <div className="text-2xl font-bold">100,000</div>
              <div className="text-sm text-muted-foreground">Token Budget</div>
            </div>
            <div className="p-4 bg-black/30 rounded-lg text-center">
              <div className="text-2xl font-bold">30</div>
              <div className="text-sm text-muted-foreground">Image Cap</div>
            </div>
            <div className="p-4 bg-black/30 rounded-lg text-center">
              <div className="text-2xl font-bold">2</div>
              <div className="text-sm text-muted-foreground">Video Cap</div>
            </div>
          </CardContent>
        </Card>

        {/* Environment Variables Reference */}
        <Card className="bg-card border-border card-glow">
          <CardHeader>
            <CardTitle className="text-base">Environment Variables</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>Set these as environment secrets for production use:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
              {[
                'OPENAI_API_KEY',
                'KAZILINK_API_URL',
                'KAZILINK_API_KEY',
                'SITEDESK_API_URL',
                'SITEDESK_API_KEY',
                'JWT_SECRET_KEY',
                'FLUTTERWAVE_SECRET_KEY',
                'WHATSAPP_ACCESS_TOKEN',
              ].map(v => (
                <div key={v} className="bg-black/30 rounded px-3 py-1.5 font-mono text-xs text-foreground/70">
                  {v}
                </div>
              ))}
            </div>
            <p className="text-xs mt-3">
              Connector settings saved via the UI above are stored in the database and override environment variables at runtime.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
